/**
 * CONTRACT FILE. Destination: src/app/dialogs/types.ts
 * Cross-package dialog ids, props and RESULTS. A package that OWNS a dialog registers it in its FeatureModule
 * (`dialogs: [{ id: DIALOG.easingPicker, kind: 'popover', component: EasingPicker }]`); any other package opens it
 * with `await services.ui.openDialog(DIALOG.easingPicker, props)` and gets the typed result.
 * Every result type includes `null` = dismissed (Esc / close); the DialogHost resolves null on dismissal.
 * Dialogs used only inside the owning package are rendered by that package directly and are not listed here.
 *
 * Owners: easingPicker, bezierEditor, templates -> W2-05; importReport, exportDialog, skinInstall -> W2-09;
 * lineDelete, parentPrompt, lineType -> W2-07; distribute, clone, fill, motionPath, pasteSpecial, transform, bake,
 * flatten -> W2-11; versions -> W2-10; keymapRecorder -> W2-13; wizard, firstRun, tour, help -> W2-14;
 * textMode, projectSettings -> W2-06; latencyCalibration -> W2-08.
 */

import type { Beat } from '../../core/time/types';
import type { BezierPoints, Ease, EaseUse } from '../../core/ease/types';
import type { AssetId, KeyId, LineId, LineProp, NoteId, ProjectId, UiElement } from '../../core/model/types';
import type { ImportedAsset, ImportResult } from '../../formats/types';
import type { RespackFiles, RespackValidation } from '../../formats/respack/types';
import type { ClipboardPayload, LineTypeChoice, PasteMode } from '../../core/commands/types';
import type { TemplateId } from '../../core/tools/types';
import type { Json, Locale } from '../../core/util/types';

export const DIALOG = {
  easingPicker: 'easingPicker',
  bezierEditor: 'bezierEditor',
  templates: 'templates',
  importReport: 'importReport',
  exportDialog: 'exportDialog',
  skinInstall: 'skinInstall',
  lineDelete: 'lineDelete',
  parentPrompt: 'parentPrompt',
  lineType: 'lineType',
  distribute: 'distribute',
  clone: 'clone',
  fill: 'fill',
  motionPath: 'motionPath',
  pasteSpecial: 'pasteSpecial',
  transform: 'transform',
  bake: 'bake',
  flatten: 'flatten',
  versions: 'versions',
  keymapRecorder: 'keymapRecorder',
  wizard: 'wizard',
  firstRun: 'firstRun',
  tour: 'tour',
  help: 'help',
  textMode: 'textMode',
  projectSettings: 'projectSettings',
  latencyCalibration: 'latencyCalibration',
} as const;

export type DialogId = (typeof DIALOG)[keyof typeof DIALOG];

export interface EasingPickerProps {
  /** Segments (start keys) the choice applies to; the live preview animates the first. */
  readonly segments: readonly KeyId[];
  readonly initial: Ease | null;
  readonly use: EaseUse;
}
/** Resolves to the chosen Ease, or null on Esc (nothing changed until Enter/click). The CALLER applies it (setEase). */
export type EasingPickerResult = Ease | null;

export interface BezierEditorProps {
  readonly initial: BezierPoints;
  /** Segments the curve is meant for (collision warning, live preview). */
  readonly segments: readonly KeyId[];
}

export interface TemplatesProps {
  readonly line: LineId;
  readonly at: Beat;
  readonly initial?: TemplateId;
  /** A point picked in the preview (RPE px, the line's parent space): "Move here", slide from / to a point. */
  readonly point?: readonly [x: number, y: number];
}

export interface ImportReportProps {
  readonly result: Extract<ImportResult, { readonly kind: 'chart' }>;
  /** Offer "Add into the current project" when a project is open. */
  readonly canMerge: boolean;
}
/** 'merge': add into the open project at `beatOffset`; `align` is the placement choice (MergeCommands.mergeChart). */
export type ImportReportResult =
  | { readonly action: 'open' }
  | { readonly action: 'merge'; readonly beatOffset: Beat; readonly align?: 'auto' | 'beats' | 'musicTime' }
  | null;

export interface ExportDialogProps {
  readonly projectId: ProjectId;
  /** Ctrl+E then Enter: focus the Export button and run with the last settings. */
  readonly quick: boolean;
}

export interface SkinInstallProps {
  readonly pack: RespackFiles | null;
  readonly validation: RespackValidation;
}

export interface LineDeleteProps {
  readonly lines: readonly LineId[];
}

export interface ParentPromptProps {
  readonly line: LineId;
  readonly parent: LineId | null;
}
export type ParentPromptResult = 'keepNumbers' | 'keepPlace' | null;

export interface LineTypeProps {
  readonly line: LineId;
  /** Pre-selected target type (e.g. from the inspector's Type dropdown). */
  readonly target?: LineTypeChoice;
  /** Image for image / GIF targets, already chosen by the caller (pre-selected in the dialog). */
  readonly texture?: AssetId;
  /** HUD element for the UI mover target, already chosen by the caller (pre-selected in the dialog). */
  readonly attachUI?: UiElement;
}

export interface GeneratorSheetProps {
  readonly notes: readonly NoteId[];
  readonly segments: readonly KeyId[];
  readonly line: LineId | null;
  /** Keyframes selected as points (Distribute works on keys as well as segments). */
  readonly keys?: readonly KeyId[];
}

export interface PasteSpecialProps {
  readonly payload: ClipboardPayload;
  readonly targetLine: LineId;
}
/** The dialog only CHOOSES; W2-03's paste flow performs the paste (ghost or immediate) with these settings. */
export type PasteSpecialResult = {
  readonly mode: PasteMode;
  readonly anchor: 'mouse' | 'playhead' | Beat;
  readonly targetLine: LineId;
  readonly xShift: number;
} | null;

export interface TransformProps {
  readonly notes: readonly NoteId[];
  readonly keys: readonly KeyId[];
  readonly pivotBeat?: Beat;
}

export interface FlattenProps {
  readonly line: LineId;
  readonly prop: LineProp;
}

export interface VersionsProps {
  readonly projectId: ProjectId;
}

export interface KeymapRecorderProps {
  readonly command: string;
  /** Chord being replaced, if any. */
  readonly current?: string;
  /** Arguments of the binding (commands bound once per argument set, e.g. beat snap 1/4, 1/8, ...). */
  readonly args?: Json;
  /** Condition of a new binding (defaults to the command's existing bindings). */
  readonly when?: string;
}

export interface WizardProps {
  /** Pre-filled media from a drop or the Library (already imported by the io worker). */
  readonly music?: ImportedAsset;
  readonly illustration?: ImportedAsset;
}

export interface HelpProps {
  readonly topic?: string;
}

export interface TextModeProps {
  readonly segment: KeyId;
  /** Pre-selected mode (a one-click mode switch that needs texts first). */
  readonly mode?: 'switch' | 'typeOn' | 'erase' | 'extend' | 'counter';
}

export interface ProjectSettingsProps {
  readonly section?: 'display' | 'gameplay' | 'speed' | 'preview' | 'export';
}

/** Outcome of a dialog that runs its own undoable transaction (null = cancelled, nothing changed). */
export type AppliedResult = { readonly applied: true } | null;

/**
 * Props and result per dialog id. `DialogMapComplete` below fails to compile when an id is missing.
 */
export interface DialogMap {
  easingPicker: { props: EasingPickerProps; result: EasingPickerResult };
  bezierEditor: { props: BezierEditorProps; result: BezierPoints | null };
  /** Applies the template as one transaction inside the dialog (live gesture preview). */
  templates: { props: TemplatesProps; result: AppliedResult };
  importReport: { props: ImportReportProps; result: ImportReportResult };
  exportDialog: { props: ExportDialogProps; result: { readonly fileName: string } | null };
  skinInstall: { props: SkinInstallProps; result: { readonly installedId: string } | null };
  /** Runs Core.lines.deleteLines with the chosen children policy. */
  lineDelete: { props: LineDeleteProps; result: AppliedResult };
  parentPrompt: { props: ParentPromptProps; result: ParentPromptResult };
  /** Explains data loss, then runs Core.lines.setLineType. */
  lineType: { props: LineTypeProps; result: AppliedResult };
  distribute: { props: GeneratorSheetProps; result: AppliedResult };
  clone: { props: GeneratorSheetProps; result: AppliedResult };
  fill: { props: GeneratorSheetProps; result: AppliedResult };
  motionPath: { props: GeneratorSheetProps; result: AppliedResult };
  /** `mode`: open on Bake (default) or Simplify (Fit; e.g. the "Simplify..." action when Join cannot merge exactly). */
  bake: { props: GeneratorSheetProps & { readonly mode?: 'bake' | 'simplify' }; result: AppliedResult };
  flatten: { props: FlattenProps; result: AppliedResult };
  pasteSpecial: { props: PasteSpecialProps; result: PasteSpecialResult };
  /** Runs Core.transform.transform. */
  transform: { props: TransformProps; result: AppliedResult };
  versions: { props: VersionsProps; result: null };
  keymapRecorder: { props: KeymapRecorderProps; result: { readonly chord: string } | null };
  /**
   * Resolves `{ projectId }` after Create. The wizard itself then opens the new chart in the Chart workspace (after
   * resolving, since opening a project dismisses dialogs), asks for persistent storage on the first project and
   * offers the tour; openers must not open the project again.
   */
  wizard: { props: WizardProps; result: { readonly projectId: ProjectId } | null };
  firstRun: { props: null; result: { readonly level: 'guided' | 'full'; readonly preset: 'default' | 'rpe'; readonly locale: Locale } | null };
  tour: { props: { readonly startAt?: number }; result: null };
  help: { props: HelpProps; result: null };
  /** Runs Core.lines.setTextSegmentMode. */
  textMode: { props: TextModeProps; result: AppliedResult };
  projectSettings: { props: ProjectSettingsProps; result: null };
  /** Writes settings.audio.latency.calibrationMs itself; returns the measured value. */
  latencyCalibration: { props: null; result: { readonly calibrationMs: number } | null };
}

/** Compile-time completeness check: every DialogId has an entry. */
export type DialogMapComplete = { readonly [K in DialogId]: DialogMap[K] };
